import { Tooltip as FluentTooltip } from "@fluentui/web-components";
/**
 * Tooltip
 * @summary A custom HTML Tooltip element based on Fluent UI's Tooltip, enhanced with custom styling and behavior.
 *
 * @example
 * ```html
 * <fabric-tooltip id="exampleTooltip" anchor="buttonId" delay="300" positioning="above">
 *   Tooltip content
 * </fabric-tooltip>
 * ```
 *
 * @attr {string} id - The item ID.
 * @attr {number | null} delay - Set the delay for the tooltip to appear.
 * @attr {string} positioning - Set the positioning of the tooltip relative to the anchor element.
 * @attr {string} anchor - The id of the anchor element for the tooltip.
 *
 * @prop {string} id - The item ID.
 * @prop {number | null} delay - Set the delay for the tooltip to appear.
 * @prop {string} positioning - Set the positioning of the tooltip relative to the anchor element.
 * @prop {string} anchor - The id of the anchor element for the tooltip.
 *
 * @slot - The default slot for the tooltip content.
 *
 * @cssprop --block-offset - Offset applied along the block axis for tooltip positioning.
 * @cssprop --inline-offset - Offset applied along the inline axis for tooltip positioning.
 * @cssprop --position-area - CSS position area used to place the tooltip.
 * @cssprop --position-try-options - CSS position-try options for tooltip fallback placement.
 *
 * @extends FluentTooltip
 * @tagname fabric-tooltip
 * @public
 */
export class Tooltip extends FluentTooltip {
    /**
     * Resolves the anchor element from the DOM.
     */
    get resolvedAnchorElement() {
        var _a;
        const rootNode = this.getRootNode();
        return (rootNode instanceof ShadowRoot || rootNode instanceof Document
            ? rootNode
            : document).getElementById((_a = this.anchor) !== null && _a !== void 0 ? _a : "");
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        clearTimeout(this.showTimer);
        this.showTimer = undefined;
        clearTimeout(this.hideTimer);
        this.hideTimer = undefined;
    }
    /**
     * Shows the tooltip after the specified delay.
     * Cancels any pending hide timer to prevent conflicts.
     * @param delay - Number of milliseconds to delay showing the tooltip
     * @internal
     */
    showTooltip(delay) {
        var _a;
        clearTimeout(this.hideTimer);
        this.hideTimer = undefined;
        clearTimeout(this.showTimer);
        const resolvedDelay = (_a = delay !== null && delay !== void 0 ? delay : this.delay) !== null && _a !== void 0 ? _a : Tooltip.DEFAULT_DELAY;
        this.showTimer = setTimeout(() => {
            this.showTimer = undefined;
            this.setAttribute("aria-hidden", "false");
            this.showPopover();
        }, resolvedDelay);
    }
    /**
     * Hides the tooltip after the specified delay.
     * Cancels any pending show timer so the tooltip won't appear after the mouse leaves.
     * @param delay - Number of milliseconds to delay hiding the tooltip
     * @internal
     */
    hideTooltip(delay) {
        var _a;
        clearTimeout(this.showTimer);
        this.showTimer = undefined;
        clearTimeout(this.hideTimer);
        const resolvedDelay = (_a = delay !== null && delay !== void 0 ? delay : this.delay) !== null && _a !== void 0 ? _a : Tooltip.DEFAULT_DELAY;
        this.hideTimer = setTimeout(() => {
            var _a;
            this.hideTimer = undefined;
            if (this.matches(":hover") ||
                ((_a = this.resolvedAnchorElement) === null || _a === void 0 ? void 0 : _a.matches(":hover"))) {
                this.hideTooltip(resolvedDelay);
                return;
            }
            this.setAttribute("aria-hidden", "true");
            this.hidePopover();
        }, resolvedDelay);
    }
}
Tooltip.DEFAULT_DELAY = 250;
//# sourceMappingURL=tooltip.js.map